<template>
  <b-modal ref="modal" hide-footer title="Borrow Stock" static>
    <div v-if="product">
      <p class="mb-3">
        <span class="font-weight-bold">{{ product.product }}</span>
        <span class="text-muted d-block">Out of stock — borrow to fulfil this sale.</span>
      </p>

      <form v-if="step === 'form'" @submit.prevent="goToReview">
        <div class="form-row">
          <div class="col-6 form-group">
            <label>Quantity Borrowed</label>
            <input type="number" min="0.01" step="any" class="form-control" v-model.number="form.quantity" />
          </div>
          <div class="col-6 form-group">
            <label>Agreed Cost Price</label>
            <input type="number" min="0" step="any" class="form-control" v-model.number="form.cost_price" />
            <small class="text-muted">Your inventory buying price is {{ convertCurrencyToLocal(currentBuyingPrice) }}. This can be different — it won't change your inventory price.</small>
          </div>
        </div>

        <div class="form-group">
          <label>Selling Price</label>
          <input type="number" min="0" step="any" class="form-control" v-model.number="form.selling_price" />
        </div>

        <div class="form-group">
          <label class="font-weight-bold">Payment Status</label>
          <div class="border rounded p-2">
            <div class="custom-control custom-radio mb-2">
              <input type="radio" id="bsm-pay-later" class="custom-control-input" value="pay_later" v-model="form.payment_status">
              <label class="custom-control-label" for="bsm-pay-later">Pay Later</label>
            </div>
            <div class="custom-control custom-radio">
              <input type="radio" id="bsm-pay-now" class="custom-control-input" value="paid_now" v-model="form.payment_status">
              <label class="custom-control-label" for="bsm-pay-now">Paid Now</label>
            </div>
          </div>
        </div>

        <!-- Naming who it's from is opt-in via the checkbox for Paid Now (nothing
             owed, so it's optional) - but forced open for Pay Later, since there
             has to be someone to collect the debt info from. -->
        <div class="form-group custom-control custom-checkbox" v-if="form.payment_status === 'paid_now'">
          <input type="checkbox" class="custom-control-input" id="bsm-track-supplier" v-model="trackSupplier">
          <label class="custom-control-label" for="bsm-track-supplier">Note who I'm borrowing from</label>
        </div>

        <div v-if="showSupplierFields" class="form-group">
          <label>{{ form.payment_status === 'pay_later' ? 'Who are you borrowing from?' : 'Supplier/Shop Name' }}</label>
          <input type="text" class="form-control" v-model="supplierName" placeholder="e.g. Neighbor's shop, ABC Suppliers" />
          <a v-if="!showPhoneField" href="#" class="small" @click.prevent="showPhoneField = true">+ Add phone number</a>
          <input v-else type="text" class="form-control mt-2" v-model="supplierPhone" placeholder="Phone number (optional)" />
        </div>

        <!-- "Covered by" - only relevant once money is actually changing hands.
             Cash isn't a separate choice - it's just one of the company's
             configured Payment Methods below, same as M-Pesa or Bank. -->
        <div v-if="form.payment_status === 'paid_now'">
          <label class="font-weight-bold">Covered by</label>
          <div class="border rounded p-2 mb-2">
            <div class="custom-control custom-radio mb-2">
              <input type="radio" id="bsm-fs-pm" class="custom-control-input" value="payment_method" v-model="funding.funding_source">
              <label class="custom-control-label" for="bsm-fs-pm">
                Business Payment Method
                <small class="text-muted d-block">Cash, M-Pesa, Bank, Card, etc.</small>
              </label>
            </div>
            <div class="custom-control custom-radio">
              <input type="radio" id="bsm-fs-ext" class="custom-control-input" value="external" v-model="funding.funding_source">
              <label class="custom-control-label" for="bsm-fs-ext">
                External Source
                <small class="text-muted d-block">Director's account, investor, loan — business cash not affected</small>
              </label>
            </div>
          </div>

          <div class="form-group" v-if="funding.funding_source === 'payment_method'">
            <label>Payment Method Used</label>
            <select class="form-control" v-model="funding.payment_type_id">
              <option :value="null">Select payment method</option>
              <option v-for="pt in paymentTypes" :key="pt.id" :value="pt.id">{{ pt.payment_method }}</option>
            </select>
          </div>

          <div class="form-group" v-if="funding.funding_source === 'external'">
            <label>Source Name</label>
            <input type="text" class="form-control" v-model="funding.external_source_label" placeholder="e.g. Director's Account, KCB Loan" />
          </div>
        </div>

        <div class="form-group" v-if="form.payment_status === 'pay_later'">
          <label>Due Date <small class="text-muted">(optional)</small></label>
          <date-range-picker :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true" v-model="dueDatePicker" class="custom-width-datepicker">
            <template #input="picker">
              <span v-if="picker.startDate" class="text-dark">{{ formatPickerDate(picker.startDate) }}</span>
              <span v-else class="text-muted"><i class="bi bi-calendar-event mr-1"></i>Select date</span>
            </template>
          </date-range-picker>
        </div>

        <div class="form-group">
          <label>Notes <small class="text-muted">(optional)</small></label>
          <textarea class="form-control" rows="2" v-model="form.notes"></textarea>
        </div>

        <div v-if="error" class="text-danger mb-2">{{ error }}</div>

        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-light mr-2" @click="$refs.modal.hide()">Cancel</button>
          <button type="submit" class="btn btn-primary">Next: Review</button>
        </div>
      </form>

      <div v-else>
        <div class="border rounded p-3 mb-3">
          <p class="font-weight-bold mb-2">Review before adding to inventory</p>

          <div class="d-flex justify-content-between mb-1">
            <span>Quantity Borrowed</span>
            <span class="font-weight-bold">{{ form.quantity }}</span>
          </div>

          <div class="d-flex justify-content-between mb-1 mt-2">
            <span>Agreed Cost Price <small class="text-muted">(this batch)</small></span>
            <span class="font-weight-bold">{{ convertCurrencyToLocal(form.cost_price) }}</span>
          </div>
          <div class="d-flex justify-content-between mb-1 text-muted small">
            <span>Your inventory buying price</span>
            <span>{{ convertCurrencyToLocal(currentBuyingPrice) }}</span>
          </div>

          <div class="d-flex justify-content-between mb-1 mt-2">
            <span>Selling Price <small class="text-muted">(this batch)</small></span>
            <span class="font-weight-bold">{{ convertCurrencyToLocal(form.selling_price) }}</span>
          </div>
          <div class="d-flex justify-content-between mb-1 text-muted small">
            <span>Your inventory selling price</span>
            <span>{{ convertCurrencyToLocal(currentSellingPrice) }}</span>
          </div>

          <div v-if="priceDiffers" class="alert alert-warning mt-2 mb-0 py-2 px-2 small">
            This batch's price is different from what's already in inventory — that's fine, it's tracked separately for this borrow only and won't change your product's Buying/Selling Price.
          </div>
        </div>

        <p class="small text-muted">
          Confirming adds {{ form.quantity }} unit(s) of <strong>{{ product.product }}</strong> to your inventory now, borrowed from
          {{ supplierName || 'an unnamed source' }}{{ form.payment_status === 'pay_later' ? ' (owed, pay later)' : ' (paid now)' }}.
        </p>

        <div v-if="error" class="text-danger mb-2">{{ error }}</div>

        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-light mr-2" @click="step = 'form'" :disabled="loading">Back</button>
          <button type="button" class="btn btn-primary" :disabled="loading" @click="submit">
            {{ loading ? 'Adding...' : 'Confirm & Add to Inventory' }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';

export default {
  name: 'BorrowStockModal',
  components: { DateRangePicker },

  data() {
    return {
      product: null,
      paymentTypes: [],
      loading: false,
      error: '',
      step: 'form',
      trackSupplier: false,
      showPhoneField: false,
      supplierName: '',
      supplierPhone: '',
      form: {
        quantity: 1,
        cost_price: 0,
        selling_price: 0,
        payment_status: 'pay_later',
        due_date: null,
        notes: '',
      },
      funding: {
        funding_source: 'payment_method',
        payment_type_id: null,
        external_source_label: '',
      },
    };
  },

  computed: {
    // Paying later always needs a name to collect from; paying now is opt-in
    // via the checkbox since there's no debt to track.
    showSupplierFields() {
      return this.form.payment_status === 'pay_later' || this.trackSupplier;
    },
    dueDatePicker: {
      get() { return { startDate: this.form.due_date ? new Date(this.form.due_date) : null }; },
      set(val) { this.form.due_date = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : null; },
    },

    currentBuyingPrice() {
      return parseFloat(this.product && this.product.wholesale_ppu) || 0;
    },

    currentSellingPrice() {
      return parseFloat(this.product && (this.product.effective_price || this.product.ppu)) || 0;
    },

    priceDiffers() {
      return this.form.cost_price !== this.currentBuyingPrice || this.form.selling_price !== this.currentSellingPrice;
    },
  },

  methods: {
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    open(product, prefillQty) {
      this.product = product;
      this.error = '';
      this.loading = false;
      this.step = 'form';
      this.trackSupplier = false;
      this.showPhoneField = false;
      this.supplierName = '';
      this.supplierPhone = '';
      this.form = {
        quantity: prefillQty && prefillQty > 0 ? prefillQty : 1,
        cost_price: 0,
        selling_price: parseFloat(product.effective_price || product.ppu) || 0,
        payment_status: 'pay_later',
        due_date: null,
        notes: '',
      };
      this.funding = { funding_source: 'payment_method', payment_type_id: null, external_source_label: '' };

      this.$refs.modal.show();
    },

    async fetchPaymentTypes() {
      try {
        const { data } = await axios.get('/api/payments');
        this.paymentTypes = Array.isArray(data) ? data.filter(p => p.enabled) : [];
      } catch (e) {
        this.paymentTypes = [];
      }
    },

    goToReview() {
      this.error = '';

      if (!this.form.quantity || this.form.quantity <= 0) {
        this.error = 'Enter the quantity borrowed.';
        return;
      }
      if (this.showSupplierFields && !this.supplierName.trim()) {
        this.error = this.form.payment_status === 'pay_later'
          ? "Enter who you're borrowing from so you know who to pay back."
          : 'Enter a name, or leave the checkbox unticked to skip it.';
        return;
      }
      if (this.form.payment_status === 'paid_now' && this.funding.funding_source === 'payment_method' && !this.funding.payment_type_id) {
        this.error = 'Select the payment method used.';
        return;
      }
      if (this.form.payment_status === 'paid_now' && this.funding.funding_source === 'external' && !this.funding.external_source_label) {
        this.error = "Name the external source (e.g. Director's Account).";
        return;
      }

      this.error = '';
      this.step = 'review';
    },

    // Only reachable via the explicit "Confirm & Add to Inventory" button on
    // the review step — this is what actually merges the borrow into
    // inventory, so it must never fire automatically or from step "form".
    async submit() {
      const payload = {
        product_id: this.product.id,
        quantity: this.form.quantity,
        cost_price: this.form.cost_price,
        selling_price: this.form.selling_price,
        payment_status: this.form.payment_status,
        due_date: this.form.due_date || null,
        notes: this.form.notes || null,
        supplier: this.supplierName.trim()
          ? { supplier: this.supplierName.trim(), phone: this.supplierPhone || null }
          : null,
        funding: this.form.payment_status === 'paid_now' ? this.funding : undefined,
      };

      this.loading = true;
      try {
        const { data } = await axios.post('/api/borrowed-stock', payload);
        this.$refs.modal.hide();
        this.$emit('borrowed', { borrowedStock: data.borrowed_stock, product: data.product, quantity: this.form.quantity });
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || 'Could not record the borrow.';
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.fetchPaymentTypes();
  },
};
</script>
